import { styled } from '@mui/material/styles';
import { Dialog, Box, Typography, Button } from '@mui/material';

interface VoucherPaymentStyledDialogProps {
  width?: string;
}

// ========================================
// Add/Edit Dialog Styles
// ========================================

export const VoucherPaymentStyledDialog = styled(
  Dialog
)<VoucherPaymentStyledDialogProps>(({ width }) => ({
  '& .MuiDialog-container': {
    justifyContent: 'center',
    alignItems: 'center',
  },
  '& .MuiDialog-paper': {
    width: width || '600px',
    height: '600px',
    maxWidth: 'none',
    maxHeight: '90vh',
    margin: 0,
    overflow: 'hidden',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
}));

export const VoucherPaymentRelativePositionBox = styled(Box)({
  position: 'relative',
});

export const VoucherPaymentCloseIcon = styled(Box)({
  position: 'absolute',
  top: '-8px',
  right: '-8px',
  fontSize: '24px',
  cursor: 'pointer',
  color: 'var(--instructions)',
  '&:hover': {
    color: 'var(--error)',
  },
}) as typeof Box;

export const VoucherPaymentFormContentBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

export const VoucherPaymentScrollableFormContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingRight: '8px',
  minHeight: 0,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8',
  },
});

export const VoucherPaymentButtonContainer = styled(Box)({
  display: 'flex',
  gap: '12px',
  marginTop: 'auto',
  paddingTop: '24px',
  flexShrink: 0,
});

export const VoucherPaymentCancelButton = styled(Button)({
  backgroundColor: 'var(--inactive-btn)',
  color: 'var(--primary-navy)',
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '8px 24px',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: 'var(--hover-white)',
  },
});

export const VoucherPaymentSubmitButton = styled('button')({
  height: '40px',
  backgroundColor: 'var(--primary-blue)',
  color: 'white',
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '8px 24px',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'var(--primary-blue)',
    filter: 'brightness(0.9)',
  },
  '&:disabled': {
    backgroundColor: 'var(--primary-blue)',
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

export const VoucherPaymentBorderedSectionBox = styled(Box)({
  border: '1px solid var(--border)',
  borderRadius: '8px',
  padding: '16px',
  paddingBottom: '0px',
  marginTop: '8px',
});

export const VoucherPaymentSectionTitleTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--subtext)',
  marginBottom: 10,
});

export const VoucherPaymentHeaderBoxSimple = styled(Box)({
  marginBottom: '10px',
  flexShrink: 0,
});

export const VoucherPaymentTitleTypographyLarge = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '24px',
  fontWeight: 600,
  color: 'var(--primary-blue)',
});

export const VoucherPaymentSubtitleTypographySimple = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 400,
  color: 'var(--subtext)',
});

export const VoucherPaymentFormContainer = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
  minHeight: 0,
});

export const VoucherPaymentStyledCircularProgress = styled('div')({
  '& .MuiCircularProgress-root': {
    color: 'white',
  },
});

// ========================================
// View Dialog Styles - Slide-in panel
// ========================================

export const VoucherPaymentViewStyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    position: 'fixed',
    right: 0,
    top: '100px',
    bottom: 0,
    width: '940px',
    height: 'auto',
    margin: 0,
    maxWidth: 'none',
    maxHeight: 'none',
    borderRadius: 0,
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
  },
});

export const VoucherPaymentFixedHeaderWrapper = styled(Box)({
  position: 'sticky',
  top: 0,
  background: '#ffffff',
  zIndex: 10,
  padding: '20px 32px 0',
  flexShrink: 0,
});

export const VoucherPaymentContentWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '10px 32px 20px',

  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8',
  },
});

export const VoucherPaymentHeaderSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: '16px',
  flexShrink: 0,
});

export const VoucherPaymentHeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const VoucherPaymentViewTitleTypography = styled(Typography)({
  fontFamily: "'Lufga', sans-serif",
  fontWeight: 500,
  fontSize: '32px',
  lineHeight: '100%',
  color: '#0F52BA',
});

export const VoucherPaymentViewCloseIcon = styled(Box)({
  width: '24px',
  height: '24px',
  color: '#75838A',
  cursor: 'pointer',
  fontSize: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    color: '#0B2330',
  },
}) as typeof Box;

export const VoucherPaymentMainContentGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 325px',
  gap: '16px',
  alignItems: 'start',
});

export const VoucherPaymentDetailsBox = styled(Box)({
  background: '#FAFAFA',
  borderRadius: '8px',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const VoucherPaymentInfoRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

export const VoucherPaymentInfoBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const VoucherPaymentInfoBlockRight = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '8px',
});

export const VoucherPaymentMethodCard = styled(Box)({
  background: '#FFFFFF',
  borderRadius: '4px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
});

export const VoucherPaymentMethodRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 0',
  '&:first-of-type': {
    paddingTop: '0',
  },
  '&:last-of-type': {
    paddingBottom: '0',
  },
});

export const VoucherPaymentMethodDivider = styled(Box)({
  width: '100%',
  height: '0px',
  borderTop: '1px solid #F0F0F0',
});

export const VoucherPaymentStatusInfoBox = styled(Box)({
  border: '1px solid #DFDFDF',
  borderRadius: '8px',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const VoucherPaymentStatusInfoRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

export const VoucherPaymentRejectionNoteBox = styled(Box)({
  background: 'rgba(232, 94, 81, 0.08)',
  borderRadius: '4px',
  padding: '8px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const VoucherPaymentRejectionLabelTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#E85E51',
});

export const VoucherPaymentLabelTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#75838A',
});

export const VoucherPaymentValueTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#0B2330',
});

export const VoucherPaymentSmallValueTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '100%',
  color: '#0B2330',
});

export const VoucherPaymentDetailLabelText = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#555555',
});

export const VoucherPaymentDetailValueText = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#0B2330',
});

export const VoucherPaymentNoteText = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '140%',
  color: '#0B2330',
});

// ========================================
// Form field layout styled components
// ========================================

export const VoucherPaymentFieldRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '16px',
  marginBottom: 0,
});

export const VoucherPaymentFieldItem = styled(Box)({
  flex: 1,
});

export const VoucherPaymentFieldItemEnd = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
});

export const VoucherPaymentChequeRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '16px',
  marginBottom: 0,
  marginTop: '-24px',
});

export const VoucherPaymentChequeDateContainer = styled(Box)({
  marginBottom: '24px',
});

export const VoucherPaymentChequeDateLabel = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '14px',
  color: 'var(--subtext)',
  marginBottom: '16px',
});

export const VoucherPaymentBankRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 0,
  gap: '16px',
});

export const VoucherPaymentOnlineRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 0,
  marginTop: '-24px',
  gap: '16px',
});
